/**
 * Single-use `jti` replay store for the bound gateway-HMAC bundle (D644 § 1).
 *
 * D644 chose the STRICTER posture: a per-bundle `jti` nonce that is tracked
 * server-side with a TTL so a second presentation of the same `jti` is
 * rejected — replay is defeated even INSIDE the (short) `exp` validity window,
 * not just after it. That requires an atomic check-and-reserve keyed on `jti`.
 *
 * R1 — no Noop/in-memory DEFAULT is wired into a shipped verify path. The
 * verifier ({@link verifyBoundGatewayBundle}) REQUIRES a store; a service that
 * turns on bound verification MUST inject a real one. The shipped real
 * implementation is {@link RedisGatewayBundleReplayStore} (atomic `SET … NX PX`
 * against the platform Redis every gateway consumer already runs). The
 * in-memory {@link InMemoryGatewayBundleReplayStore} is a TEST DOUBLE only —
 * process-local, lost on restart, and never a production default.
 *
 * Refs: D644 § 1 (jti single-use nonce), D356 (the secret lifecycle this reuses).
 */

/**
 * Atomic reserve-or-reject for a bundle `jti`.
 *
 * A single method by design: replay defence needs check-and-set to be ONE
 * atomic step (a separate "has this jti?" + "record it" races two concurrent
 * replays through). Implementations MUST make {@link reserve} atomic.
 */
export interface GatewayBundleReplayStore {
  /**
   * Reserve `jti` for `ttlMs`. Returns `true` iff `jti` was previously unseen
   * (it is now reserved and any later `reserve(jti, …)` within the TTL returns
   * `false`); `false` iff `jti` was already reserved — i.e. a replay.
   *
   * `ttlMs` should be the remaining validity of the bundle (time until `exp`
   * plus the clock-skew allowance): the store only needs to remember a `jti`
   * for as long as a bundle carrying it could still verify.
   */
  reserve(jti: string, ttlMs: number): Promise<boolean>;
}

/**
 * The minimal Redis client surface {@link RedisGatewayBundleReplayStore} needs
 * — the `SET key value PX <ms> NX` form. Both `ioredis` (`set(k, v, "PX", ms,
 * "NX")`) and `node-redis` v4 (`set(k, v, { PX: ms, NX: true })`) satisfy one of
 * the two call shapes below, so the store adapts to either without this package
 * taking a hard dependency on a specific Redis client. The injected client is
 * the SAME one the consumer already runs for idempotency / streams.
 */
export interface RedisSetNxPxClient {
  set(
    key: string,
    value: string,
    ...args: unknown[]
  ): Promise<string | null | undefined>;
}

export interface RedisGatewayBundleReplayStoreOptions {
  /** An ioredis- or node-redis-v4-compatible client (injected, already connected). */
  client: RedisSetNxPxClient;
  /**
   * Key namespace. Default `nodii:gw:jti:`. Kept short — one key per live
   * bundle, each self-expiring at `ttlMs`, so the keyspace is bounded by
   * (bundle rate × max bundle TTL). At a ~60s exp that is tiny.
   */
  keyPrefix?: string;
  /**
   * `node-redis` (v4) uses an options-object `set(k, v, { NX: true, PX: ms })`
   * and returns `null` when NX fails; `ioredis` uses variadic
   * `set(k, v, "PX", ms, "NX")` and returns `null` when NX fails. Default
   * `"ioredis"`. Both return the string `"OK"` on a successful reserve.
   */
  dialect?: "ioredis" | "node-redis";
}

/**
 * The shipped real replay store: an atomic `SET <prefix><jti> 1 PX <ttl> NX`
 * against Redis. `SET … NX` reserves the key iff absent and returns `"OK"`;
 * a replay finds the key present and gets `null`. `PX` self-expires the key at
 * the bundle's remaining validity so no sweeper is needed.
 *
 * Atomicity is Redis-native (single command), so two concurrent replays of the
 * same `jti` cannot both win.
 */
export class RedisGatewayBundleReplayStore implements GatewayBundleReplayStore {
  private readonly client: RedisSetNxPxClient;
  private readonly keyPrefix: string;
  private readonly dialect: "ioredis" | "node-redis";

  constructor(opts: RedisGatewayBundleReplayStoreOptions) {
    this.client = opts.client;
    this.keyPrefix = opts.keyPrefix ?? "nodii:gw:jti:";
    this.dialect = opts.dialect ?? "ioredis";
  }

  async reserve(jti: string, ttlMs: number): Promise<boolean> {
    // A non-positive TTL means the bundle is already expired; treat as
    // not-reservable rather than writing a zero/negative-TTL key (which Redis
    // rejects). The exp check in the verifier catches this first, so this is a
    // belt-and-braces guard.
    const px = Math.max(1, Math.floor(ttlMs));
    const key = `${this.keyPrefix}${jti}`;
    const reply =
      this.dialect === "node-redis"
        ? await this.client.set(key, "1", { NX: true, PX: px })
        : await this.client.set(key, "1", "PX", px, "NX");
    // Both clients return "OK" on a successful NX reserve, null when the key
    // already exists (a replay).
    return reply === "OK";
  }
}

/**
 * TEST DOUBLE ONLY — a process-local {@link GatewayBundleReplayStore}. Reserves
 * are held in a Map with wall-clock expiry. Never wire this into a shipped
 * verify path: it is not shared across instances and is lost on restart, so it
 * cannot actually defeat replay in production (R1). Use
 * {@link RedisGatewayBundleReplayStore} there; use this in unit tests that
 * exercise the bound-verify LOGIC without a Redis dependency.
 */
export class InMemoryGatewayBundleReplayStore
  implements GatewayBundleReplayStore
{
  private readonly seen = new Map<string, number>();

  async reserve(jti: string, ttlMs: number): Promise<boolean> {
    const now = Date.now();
    // Opportunistic sweep of expired entries (bounded work; keeps the Map from
    // growing without a timer in long test runs).
    for (const [k, exp] of this.seen) {
      if (exp <= now) this.seen.delete(k);
    }
    const existing = this.seen.get(jti);
    if (existing !== undefined && existing > now) return false;
    this.seen.set(jti, now + Math.max(1, Math.floor(ttlMs)));
    return true;
  }

  /** Test helper: drop all reservations. */
  clear(): void {
    this.seen.clear();
  }
}
